import { useState } from 'react';
import { Button } from '@mantine/core';
import { IconBellCheck, IconBellOff, IconBellRinging } from '@tabler/icons-react';
import { useDeviceRegistration } from '@/hooks/useDeviceRegistration';
import { isFirebaseConfigured } from '@/utils/firebase';
import { isNotificationSupported } from '@/utils/push';
import { useCopy } from '@/lib/settings';
import { NudgeCard } from '@/components/member/NudgeCard';
import '@/styles/member.css';

const DISMISS_KEY = 'rides.pushNudge.dismissed';

function permissionNow(): NotificationPermission {
  try {
    return isNotificationSupported() ? Notification.permission : 'denied';
  } catch {
    return 'denied';
  }
}

/**
 * Ride-day alerts on this device: whether they can be offered at all (settings switch, browser
 * support and web push configured), the current permission and a way to turn them on.
 */
export function usePushOptIn() {
  const { settings } = useCopy();
  const { registerDevice } = useDeviceRegistration();
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>(permissionNow);
  const [failed, setFailed] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const available = settings.pushOptInEnabled && isNotificationSupported() && isFirebaseConfigured();

  const enable = async () => {
    setBusy(true);
    setFailed(false);
    const r = await registerDevice();
    setPermission(permissionNow());
    setFailed(!r.isRegistered && permissionNow() !== 'denied');
    setBusy(false);
  };
  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* hide for this visit only */ }
  };

  return {
    available,
    granted: permission === 'granted',
    denied: permission === 'denied',
    /** Worth suggesting on Home: available, not decided yet and not hidden. */
    suggest: available && permission === 'default' && !dismissed,
    busy,
    failed,
    enable,
    dismiss,
  };
}

interface Props {
  /** 'nudge' (Home, hideable), 'section' (a SectionCard with the current state) or 'plain' (the same without the card, for a settings row). */
  variant?: 'nudge' | 'section' | 'plain';
  /** Anchor id for the section variant. */
  id?: string;
  /** Nudge variant: called when the member hides it (Home then shows the next suggestion). */
  onDismiss?: () => void;
}

/**
 * Ride-day alerts. Renders nothing when alerts can't be offered on this device, so Account never
 * shows an empty "Notifications" heading.
 */
export function PushOptInCard({ variant = 'nudge', id, onDismiss }: Props) {
  const { t } = useCopy();
  const push = usePushOptIn();
  if (!push.available) return null;
  if (variant === 'nudge' && !push.suggest) return null;

  const error = push.failed ? <p className="rm-small" role="status" style={{ marginTop: 6, color: 'var(--rides-bad)' }}>We couldn't turn on alerts. Please try again.</p> : null;
  const turnOn = (
    <Button variant={variant === 'nudge' ? 'default' : 'filled'} loading={push.busy} onClick={push.enable} leftSection={<IconBellRinging size={18} />}>
      Turn on alerts
    </Button>
  );

  if (variant === 'nudge') {
    return (
      <NudgeCard icon={<IconBellRinging size={24} />} title="Get ride-day alerts" action={turnOn} onDismiss={() => { push.dismiss(); onDismiss?.(); }} dismissLabel="Hide the alerts suggestion">
        {t('alerts.intro')}
      </NudgeCard>
    );
  }

  const state = (
    <>
      <NudgeCard
        flat
        headingLevel={3}
        tone={push.denied ? 'warn' : 'info'}
        icon={push.granted ? <IconBellCheck size={24} /> : push.denied ? <IconBellOff size={24} /> : <IconBellRinging size={24} />}
        title={push.granted ? 'Alerts are on' : push.denied ? 'Alerts are blocked' : 'Alerts are off'}
        action={push.granted || push.denied ? undefined : turnOn}
      >
        {push.granted ? t('alerts.on') : push.denied ? t('alerts.blocked') : t('alerts.intro')}
      </NudgeCard>
      {error}
    </>
  );
  if (variant === 'plain') {
    return (
      <div>
        <p className="rm-muted" style={{ color: 'var(--rides-ink)' }}>{push.granted ? t('alerts.on') : push.denied ? t('alerts.blocked') : t('alerts.intro')}</p>
        {!push.granted && !push.denied && <div style={{ marginTop: 12 }}>{turnOn}</div>}
        {error}
      </div>
    );
  }
  return (
    <section className="rides-card rides-sectioncard" id={id} style={{ scrollMarginTop: 80 }}>
      <div className="head">
        <div style={{ minWidth: 0 }}>
          <h2>Ride-day alerts</h2>
        </div>
      </div>
      {state}
    </section>
  );
}
